import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { BuildOutlined } from '@mui/icons-material';
import { SectionHeaderWithCommand, SectionPaper } from '../../../../common/components';
import { useAppSelector } from '../../../../../app/hooks';
import { EditorMode } from '../../../types/editorTypes';
import { 
  FacilityMaintenanceCenterEditor, 
  FacilityMaintenanceAssetLookupEditor,
  FacilityMaintenanceTitleEditor, 
  FacilityMaintenanceDescriptionEditor,
  FacilityMaintenanceLocationDetailEditor,
  FacilityMaintenanceIssueTypeEditor,
  FacilityMaintenanceRepairableByEditor,
  FacilityMaintenanceVendorAutocomplete,
  FacilityMaintenanceVendorContactDisplay,
  FacilityMaintenanceEstimatedCostEditor,
  FacilityMaintenanceEstimatedDurationEditor,
  FacilityMaintenanceActualDurationEditor
} from '../fields/facilityMaintenance';

interface FacilityMaintenanceSectionProps {
  mode?: EditorMode;
  hideEstimatedCost?: boolean;
}

// Extracted so this row's own header markup doesn't count against the section's overall line
// count (Code Health: Large Method) — mirrors PMDetailsSection's own "Asset Information" row.
const AssetRow: React.FC<{ mode: EditorMode }> = ({ mode }) => (
  <Box>
    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#64748b' }}>
      Asset
    </Typography>
    <FacilityMaintenanceAssetLookupEditor mode={mode} />
  </Box>
);

/**
 * FacilityMaintenanceSection - Unified section for facility maintenance request fields.
 * Works for both tickets (mode='ticket') and recurring tasks (mode='recurringTask').
 */
export const FacilityMaintenanceSection: React.FC<FacilityMaintenanceSectionProps> = ({ mode = 'ticket', hideEstimatedCost = false }) => {
  const repairableBy = useAppSelector(state =>
    mode === 'ticket'
      ? state.ticket.currentTicket?.facilityMaintenanceRequest?.repairableBy
      : state.recurringTask.currentRecurringTask?.facilityMaintenanceRequest?.repairableBy
  );
  const title = mode === 'ticket' ? 'Facility Maintenance Request' : 'Facility Maintenance Request Template';

  return (
    <Box>
      <SectionHeaderWithCommand
        icon={<BuildOutlined sx={{ mr: 1, color: '#3b82f6', fontSize: 18 }} />}
        title={title}
      />
      
      <SectionPaper>
        <Stack spacing={3}>
          {/* Row 1: Center + Location Detail */}
          <Box>
            <Typography 
              variant="body2" 
              sx={{ mb: 1, fontWeight: 500, color: '#64748b' }}
            >
              Location
            </Typography>
            <Stack direction="row" spacing={2}>
              <FacilityMaintenanceCenterEditor mode={mode} />
              <Box sx={{ flex: 1 }}>
                <FacilityMaintenanceLocationDetailEditor mode={mode} />
              </Box>
            </Stack>
          </Box>

          {/* Row 1b: Asset (T077, User Story 4) — mirrors PMDetailsSection's own Asset row */}
          <AssetRow mode={mode} />
          
          {/* Row 2-3: Title + Description */}
          <Box>
            <Typography 
              variant="body2" 
              sx={{ mb: 1, fontWeight: 500, color: '#64748b' }}
            >
              Maintenance Details
            </Typography>
            <Stack spacing={2}>
              <FacilityMaintenanceTitleEditor mode={mode} />
              <FacilityMaintenanceDescriptionEditor mode={mode} />
            </Stack>
          </Box>

          {/* Row 4: Issue Type + Repairable By */}
          <Box>
            <Typography 
              variant="body2" 
              sx={{ mb: 1, fontWeight: 500, color: '#64748b' }}
            >
              Classification
            </Typography>
            <Stack direction="row" spacing={2}>
              <FacilityMaintenanceIssueTypeEditor mode={mode} />
              <FacilityMaintenanceRepairableByEditor mode={mode} />
            </Stack>
          </Box>

          {/* Row 5: Vendor Info (conditional) */}
          {repairableBy === 'VENDOR' && (
            <Box>
              <Typography 
                variant="body2" 
                sx={{ mb: 1, fontWeight: 500, color: '#64748b' }}
              >
                Vendor Information
              </Typography>
              <Stack spacing={2}>
                <FacilityMaintenanceVendorAutocomplete mode={mode} />
                <FacilityMaintenanceVendorContactDisplay mode={mode} />
              </Stack>
            </Box>
          )}

          {/* Row 6: Cost Tracking (hidden when cost tab is shown in edit form) */}
          {!hideEstimatedCost && (
            <Box>
              <Typography 
                variant="body2" 
                sx={{ mb: 1, fontWeight: 500, color: '#64748b' }}
              >
                Cost Tracking
              </Typography>
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <FacilityMaintenanceEstimatedCostEditor mode={mode} />
                </Box>
                <Box sx={{ flex: 1 }} /> {/* Spacer */}
              </Stack>
            </Box>
          )}

          {/* Row 7: Duration Tracking */}
          <Box>
            <Typography 
              variant="body2" 
              sx={{ mb: 1, fontWeight: 500, color: '#64748b' }}
            >
              Duration Tracking
            </Typography>
            <Stack direction="row" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <FacilityMaintenanceEstimatedDurationEditor mode={mode} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <FacilityMaintenanceActualDurationEditor mode={mode} />
              </Box>
            </Stack>
          </Box>
        </Stack>
      </SectionPaper>
    </Box>
  );
};

export default FacilityMaintenanceSection;